import React, { useState } from "react";
import { Box, Grid2, useTheme } from "@mui/material";
import { observer } from "mobx-react-lite";
import AddIcon from "@mui/icons-material/Add";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import InlineFormError from "../../../../../../views/components/common/form/InlineFormError";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";
import {
  MAX_CLIENT_POS_PER_ORDER,
  validateClientPOFile,
} from "../../../../../../models/Order/OrderClientPO";
import { OrderClientPOStore } from "../../../../stores/OrderClientPOStore";
import ClientPOFilePicker from "./ClientPOFilePicker";

interface ClientPOAddFormProps {
  store: OrderClientPOStore;
}

/** "Add a PO" row: PO number + PDF, added one at a time. */
const ClientPOAddForm: React.FC<ClientPOAddFormProps> = observer(
  ({ store }) => {
    const theme = useTheme();
    const colors = getModernPageColors(theme);
    const [poNumber, setPONumber] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [fieldErrors, setFieldErrors] = useState<{
      poNumber?: string;
      file?: string;
    }>({});

    const limitReached = !store.canAddMore;
    const disabled = limitReached || store.isBusy;

    const handleFileSelect = (picked: File) => {
      const error = validateClientPOFile(picked);
      setFieldErrors((prev) => ({ ...prev, file: error || undefined }));
      setFile(error ? null : picked);
    };

    const handleAdd = async () => {
      const nextErrors = {
        poNumber: poNumber.trim() ? undefined : "PO number is required",
        file: validateClientPOFile(file) || undefined,
      };
      setFieldErrors(nextErrors);
      if (nextErrors.poNumber || nextErrors.file || !file) return;

      try {
        await store.addClientPO(poNumber, file);
        setPONumber("");
        setFile(null);
      } catch {
        // The store shows the error inline and keeps the uploaded file for a retry.
      }
    };

    return (
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          bgcolor: colors.surfaceSoft,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.5,
            flexWrap: "wrap",
          }}
        >
          <CustomTypography variant="heading7" color={colors.title}>
            Add a PO
          </CustomTypography>
          <CustomTypography variant="caption" color={colors.subtitle}>
            One PO at a time · PDF up to 5MB · max {MAX_CLIENT_POS_PER_ORDER}{" "}
            per order
          </CustomTypography>
        </Box>

        <Grid2 container spacing={1.5} alignItems="flex-start">
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <CustomTextField
              width="100%"
              value={poNumber}
              placeholder="PO number"
              preventEdgeSpaces
              onSearch={(value) => {
                setPONumber(value);
                if (value.trim()) {
                  setFieldErrors((prev) => ({ ...prev, poNumber: undefined }));
                }
              }}
              error={!!fieldErrors.poNumber}
              disabled={disabled}
            />
            {fieldErrors.poNumber && (
              <InlineFormError message={fieldErrors.poNumber} />
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 5 }}>
            <ClientPOFilePicker
              file={file}
              disabled={disabled}
              error={!!fieldErrors.file}
              onSelect={handleFileSelect}
              onClear={() => setFile(null)}
            />
            {fieldErrors.file && <InlineFormError message={fieldErrors.file} />}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <AppButton
              label={store.isAdding ? "Adding..." : "Add PO"}
              startIcon={<AddIcon />}
              onClick={() => void handleAdd()}
              disabled={disabled}
              customStyles={{ width: "100%", height: 40 }}
            />
          </Grid2>
        </Grid2>

        {limitReached && (
          <CustomTypography
            variant="body2"
            color={colors.subtitle}
            sx={{ mt: 1.5 }}
          >
            This order already has {MAX_CLIENT_POS_PER_ORDER} POs. Remove one to
            add another.
          </CustomTypography>
        )}
        <InlineFormError message={store.addError} sx={{ mt: 1.5, mb: 0 }} />
      </Box>
    );
  },
);

export default ClientPOAddForm;
